import React, { useMemo } from "react";
import DOMPurify from "dompurify";

import HlsVideoPlayer from "../../components/VideoPlayer/HlsVideoPlayer";
import type {
  EditorBlock,
  EditorContent,
  NewsMediaAsset,
  NewsVideoAsset,
} from "../../services/newsService/newsService.types";
import {
  ArticleBody,
  ArticleCode,
  ArticleFigure,
  ArticleImage,
  ArticleLinkBlock,
  ArticleLinkDescription,
  ArticleLinkTitle,
  ArticleQuote,
  ArticleTableWrap,
  ArticleVideo,
  MediaCaption,
  NewsFileAttachment,
  NewsFileDownload,
  NewsFileExtension,
  NewsFileInfo,
  NewsFileName,
} from "./NewsPage.styles";
import { getNewsFileDisplayName, getNewsFileExtension } from "./newsFilePresentation";

interface ArticleRendererProps {
  content: EditorContent;
  mediaAssets?: NewsMediaAsset[];
  videoAssets?: NewsVideoAsset[];
}

const safeHtml = (value: unknown) => ({
  __html: DOMPurify.sanitize(String(value || ""), {
    ALLOWED_TAGS: ["b", "strong", "i", "em", "u", "s", "strike", "del", "a", "code", "br"],
    ALLOWED_ATTR: ["href", "target", "rel"],
  }),
});

const NewsHlsVideo: React.FC<{ asset: NewsVideoAsset }> = ({ asset }) => {
  if (asset.status === "processing") {
    return (
      <ArticleVideo>
        Видео обрабатывается
        {asset.progress_percent ? " · " + asset.progress_percent + "%" : ""}
      </ArticleVideo>
    );
  }
  if (asset.status === "failed") {
    return <ArticleVideo>Не удалось обработать видео.</ArticleVideo>;
  }
  if (!asset.playlist_url) {
    return <ArticleVideo>Видео пока недоступно.</ArticleVideo>;
  }

  return (
    <HlsVideoPlayer
      className="news-article-video"
      playlistUrl={asset.playlist_url}
      videoName={asset.name}
    />
  );
};

const renderListItems = (
  items: any[],
  ordered: boolean,
  keyPrefix: string,
): React.ReactNode => {
  const Tag = ordered ? "ol" : "ul";
  return (
    <Tag>
      {(items || []).map((item, index) => {
        if (typeof item === "string") {
          return (
            <li
              key={keyPrefix + "-" + index}
              dangerouslySetInnerHTML={safeHtml(item)}
            />
          );
        }
        const content = item && (item.content || item.text || "");
        const children = item && Array.isArray(item.items) ? item.items : [];
        return (
          <li key={keyPrefix + "-" + index}>
            <span dangerouslySetInnerHTML={safeHtml(content)} />
            {children.length
              ? renderListItems(children, ordered, keyPrefix + "-" + index)
              : null}
          </li>
        );
      })}
    </Tag>
  );
};

const renderChecklistItems = (items: any[], keyPrefix: string): React.ReactNode => (
  <ul className="news-checklist">
    {(items || []).map((item, index) => {
      const content =
        typeof item === "string" ? item : item?.content || item?.text || "";
      const checked = Boolean(
        typeof item === "object" ? item?.meta?.checked ?? item?.checked : false,
      );
      const children =
        typeof item === "object" && Array.isArray(item?.items) ? item.items : [];
      return (
        <li key={keyPrefix + "-" + index}>
          <div className="news-checklist-row">
            <input
              type="checkbox"
              checked={checked}
              readOnly
              aria-label="Пункт списка"
            />
            <span dangerouslySetInnerHTML={safeHtml(content)} />
          </div>
          {children.length
            ? renderChecklistItems(children, keyPrefix + "-" + index)
            : null}
        </li>
      );
    })}
  </ul>
);

const renderBlock = (
  block: EditorBlock,
  mediaMap: Map<number, NewsMediaAsset>,
  videoMap: Map<number, NewsVideoAsset>,
  index: number,
) => {
  const data = block.data || {};
  const key = block.id || block.type + "-" + index;

  if (block.type === "paragraph") {
    return <p key={key} dangerouslySetInnerHTML={safeHtml(data.text)} />;
  }

  if (block.type === "header") {
    const level = Number(data.level) === 1 ? 1 : Number(data.level) === 3 ? 3 : 2;
    if (level === 1) {
      return <h1 key={key} dangerouslySetInnerHTML={safeHtml(data.text)} />;
    }
    if (level === 3) {
      return <h3 key={key} dangerouslySetInnerHTML={safeHtml(data.text)} />;
    }
    return <h2 key={key} dangerouslySetInnerHTML={safeHtml(data.text)} />;
  }

  if (block.type === "list") {
    if (data.style === "checklist") {
      return (
        <React.Fragment key={key}>
          {renderChecklistItems(data.items || [], key)}
        </React.Fragment>
      );
    }
    const ordered = data.style === "ordered";
    return (
      <React.Fragment key={key}>
        {renderListItems(data.items || [], ordered, key)}
      </React.Fragment>
    );
  }

  if (block.type === "checklist") {
    return (
      <React.Fragment key={key}>
        {renderChecklistItems(data.items || [], key)}
      </React.Fragment>
    );
  }

  if (block.type === "quote") {
    return (
      <ArticleQuote key={key}>
        <div dangerouslySetInnerHTML={safeHtml(data.text)} />
        {data.caption ? (
          <cite dangerouslySetInnerHTML={safeHtml(data.caption)} />
        ) : null}
      </ArticleQuote>
    );
  }

  if (block.type === "delimiter") {
    if (data.variant === "space") {
      return <div key={key} className="news-article-spacer" aria-hidden="true" />;
    }
    if (data.variant === "stars") {
      return (
        <div key={key} className="news-article-stars" aria-hidden="true">
          ***
        </div>
      );
    }
    return <hr key={key} />;
  }

  if (block.type === "code") {
    return (
      <ArticleCode key={key}>
        <code>{String(data.code || "")}</code>
      </ArticleCode>
    );
  }

  if (block.type === "table") {
    return (
      <ArticleTableWrap key={key}>
        <table>
          <tbody>
            {(data.content || []).map((row: any[], rowIndex: number) => (
              <tr key={key + "-row-" + rowIndex}>
                {(row || []).map((cell, cellIndex) => (
                  <td
                    key={key + "-cell-" + rowIndex + "-" + cellIndex}
                    dangerouslySetInnerHTML={safeHtml(cell)}
                  />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </ArticleTableWrap>
    );
  }

  if (block.type === "newsMedia") {
    const asset = mediaMap.get(Number(data.mediaId));
    if (!asset) return null;
    const caption = data.caption ? <MediaCaption>{data.caption}</MediaCaption> : null;

    if (data.kind === "image") {
      return (
        <ArticleFigure key={key}>
          <ArticleImage
            src={asset.url}
            alt={data.caption || asset.name}
            loading="lazy"
          />
          {caption}
        </ArticleFigure>
      );
    }
    if (data.kind === "audio") {
      return (
        <ArticleFigure key={key}>
          <audio controls preload="metadata" src={asset.url} />
          {caption}
        </ArticleFigure>
      );
    }

    const separator = asset.url.includes("?") ? "&" : "?";
    return (
      <ArticleFigure key={key}>
        <NewsFileAttachment>
          <NewsFileExtension aria-hidden="true">
            {getNewsFileExtension(asset.name, asset.mime)}
          </NewsFileExtension>
          <NewsFileInfo>
            <NewsFileName>{getNewsFileDisplayName(asset.name)}</NewsFileName>
            <NewsFileDownload
              href={asset.url + separator + "download=1"}
              download={asset.name}
              target="_blank"
              rel="noopener noreferrer"
            >
              Скачать файл
            </NewsFileDownload>
          </NewsFileInfo>
        </NewsFileAttachment>
        {caption}
      </ArticleFigure>
    );
  }

  if (block.type === "newsVideo") {
    const asset = videoMap.get(Number(data.videoId));
    if (!asset) return null;
    return (
      <ArticleFigure key={key}>
        <NewsHlsVideo asset={asset} />
        {data.caption ? <MediaCaption>{data.caption}</MediaCaption> : null}
      </ArticleFigure>
    );
  }

  if (block.type === "newsLink") {
    const url = String(data.url || "");
    return (
      <ArticleLinkBlock key={key}>
        <ArticleLinkTitle
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {data.title || url}
        </ArticleLinkTitle>
        {data.description ? (
          <ArticleLinkDescription>{data.description}</ArticleLinkDescription>
        ) : null}
      </ArticleLinkBlock>
    );
  }

  return null;
};

const ArticleRenderer: React.FC<ArticleRendererProps> = ({
  content,
  mediaAssets = [],
  videoAssets = [],
}) => {
  const mediaMap = useMemo(
    () => new Map(mediaAssets.map((asset) => [asset.id, asset])),
    [mediaAssets],
  );
  const videoMap = useMemo(
    () => new Map(videoAssets.map((asset) => [asset.id, asset])),
    [videoAssets],
  );

  return (
    <ArticleBody>
      {(content.blocks || []).map((block, index) =>
        renderBlock(block, mediaMap, videoMap, index),
      )}
    </ArticleBody>
  );
};

export default ArticleRenderer;